import { useCallback, useState, useEffect, useRef } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';
import { FakeLink } from '@hivebrite/design-system/Link';
import { Tooltip, POSITIONS } from '@hivebrite/design-system/Tooltip';

import Modal from 'hb-react/backoffice/components/Modal';
import Input from 'hb-react/shared/components/DesignSystem/Input';
import { useQuillEditorContext } from 'hb-react/shared/components/DesignSystem/QuillEditor/contexts/QuillEditorContext';
import ToolbarItem from 'hb-react/shared/components/DesignSystem/QuillEditor/Toolbar/components/ToolbarItem';
import LinkIcon from 'hb-react/shared/icons/Link';
import onEnterPress from 'hb-react/shared/utils/onEnterPress';
import { validateUrlAndMailToFormat } from 'hb-react/shared/utils/validators/urlFormat';

const WIDTH_LINK = 250;

const Link = ({
  label,
  onItemClick,
  onSetShowTriangle,
  onToolbarEditing,
  value,
  variant,
  ...otherProps
}) => {
  const { quillRef, displayLinkModal, setDisplayLinkModal, onAddLink } =
    useQuillEditorContext();

  const inputRef = useRef();

  const [inputError, setInputError] = useState(null);
  const [inputValue, setInputValue] = useState('');

  const hasValue = !!value;

  const handleChangeInput = useCallback((e) => {
    setInputError(null);
    setInputValue(e?.target?.value);
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleShowLinkModal = useCallback(() => setDisplayLinkModal(true), []);

  const handleSaveLink = useCallback(
    ({ remove } = { remove: false }) => {
      let valueWithProtocol = null;

      if (!remove) {
        if (!inputValue || inputValue.trim() === '') {
          return;
        }

        const error = validateUrlAndMailToFormat(inputValue);

        if (error) {
          setInputError(error);

          return;
        }

        const errorUrlProtocol = validateUrlAndMailToFormat(inputValue, true);

        if (errorUrlProtocol) {
          valueWithProtocol = `http://${inputValue}`;
        }
      }

      setDisplayLinkModal(false);

      quillRef.current.focus();

      const selection = quillRef.current.getSelection();

      const format = remove ? false : valueWithProtocol || inputValue;

      if (selection.length === 0) {
        const [leaf] = quillRef.current.getLeaf(selection.index);

        if (leaf) {
          quillRef.current.formatText(
            quillRef.current.getIndex(leaf),
            leaf.length(),
            'link',
            format,
            'user',
          );
        }
      } else {
        quillRef.current.format('link', format, 'user');
      }

      onAddLink && onAddLink(valueWithProtocol || inputValue);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inputValue],
  );

  const handleRemoveLink = useCallback(
    () => handleSaveLink({ remove: true }),
    [handleSaveLink],
  );

  useEffect(
    () => {
      if (displayLinkModal) {
        setInputValue(value);
      }

      onToolbarEditing(displayLinkModal);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [displayLinkModal, onToolbarEditing],
  );

  useEffect(() => {
    onSetShowTriangle(!hasValue);
  }, [hasValue, onSetShowTriangle]);

  if (displayLinkModal && variant !== 'builder') {
    // On LF Quill, this modal his now rendered by the Toolbar itself
  }

  if (hasValue && variant !== 'builder') {
    return (
      <Box alignItems="center" width="100%">
        <Box maxWidth={WIDTH_LINK}>
          <LinkValue>{value}</LinkValue>
        </Box>
        <Box ml={3} alignItems="center">
          <FakeLink onClick={handleShowLinkModal}>{__('Edit')}</FakeLink>
          <Box mx={2}>|</Box>
          <FakeLink onClick={handleRemoveLink}>{__('Remove')}</FakeLink>
        </Box>
      </Box>
    );
  }

  const renderItem = () => (
    <ToolbarItem
      onClick={() => {
        handleShowLinkModal();
        onItemClick();
      }}
      isActive
      icon={LinkIcon}
      {...otherProps}
    >
      {label && (
        <Box color="primary" fontWeight={1}>
          {label}
        </Box>
      )}
    </ToolbarItem>
  );

  return (
    <>
      {variant === 'builder' ? (
        <Tooltip
          initialPosition={POSITIONS.BOTTOM}
          childrenElement={value || __('Add link')}
        >
          {renderItem()}
        </Tooltip>
      ) : (
        renderItem()
      )}

      {variant === 'builder' && (
        <Modal
          isOpen={displayLinkModal}
          onClose={() => {
            onItemClick();
            setDisplayLinkModal(false);
          }}
          size="small"
        >
          <Box p={5}>
            <Box flexDirection="column" width="100%">
              <Box
                color="asphalt"
                fontSize={7}
                fontWeight={1}
                lineHeight={8}
                mb={5}
              >
                {__('Link settings')}
              </Box>
              <Input
                autoFocus
                clearable={Boolean(inputValue)}
                label={__('Add a link on your selected text')}
                error={inputError}
                onChange={handleChangeInput}
                onKeyPress={onEnterPress(handleSaveLink)}
                placeholder={__('Enter URL')}
                ref={inputRef}
                type="text"
                value={inputValue || ''}
                width="100%"
              />
              <Box justifyContent="flex-end">
                <Button
                  disabled={inputError}
                  mt={4}
                  onKeyPress={onEnterPress(() =>
                    handleSaveLink({ remove: !inputValue }),
                  )}
                  onClick={() => handleSaveLink({ remove: !inputValue })}
                >
                  {__('Save')}
                </Button>
              </Box>
            </Box>
          </Box>
        </Modal>
      )}
    </>
  );
};

Link.getFormat = (format) => format?.link;

Link.canDisplay = (quillRef) => {
  let canDisplay = false;

  // getSelection is probably better but it triggers an update which triggers handleChange
  // which in turn trigget setStates in other components during the rending of ToolbarFloat
  // which is a big no-no in React and causes an error.
  const selection = quillRef.current?.selection;

  if (selection?.length === 0) {
    canDisplay = true;

    const [blot, indexInText] = quillRef.current.getLeaf(selection.index);

    if (indexInText === 0) {
      canDisplay = false;
    } else if (blot) {
      const { text } = blot;

      if (text) {
        const cleanText = text.trim();

        // We do not display the toolbar item when the cursor is at the beginning or at the end of the text.
        if (
          (cleanText.length > 0 && cleanText.length === indexInText) ||
          indexInText === 0
        ) {
          canDisplay = false;
        }
      }
    }
  }

  return canDisplay;
};

export default Link;

const LinkValue = styled.span`
  color: asphalt;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
